import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'
import { PropsWithChildren, useEffect, useMemo } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { buildContext } from '../buildContext/buildContext'
import { createMeticulousInspectors } from '../inspector/meticulousInspector'
import type { MultiContextWithUser } from '@omnea-app/packages/universal/ld-feature-flags/src/contexts'

const LD_INITIALISATION_TIMEOUT_SECONDS = 5

const getLdConfiguration = () => {
  const clientId = process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID
  if (clientId === 'default') {
    // Local LD dev server — clientId 'default' is the ldcli project key
    // see https://launchdarkly.com/docs/guides/flags/ldcli-dev-server#configure-your-sdk-for-testing
    const localServerUrl = 'http://localhost:8765'
    return {
      clientId: 'default',
      options: {
        baseUrl: localServerUrl,
        streamUrl: localServerUrl,
        eventsUrl: localServerUrl,
        bootstrap: undefined,
      },
    }
  }

  if (!clientId) {
    throw new Error('No client id provided')
  }

  return {
    clientId,
    options: {
      bootstrap: 'localStorage' as const,
    },
  }
}

const LaunchDarklyContextUpdater = ({ children, context }: PropsWithChildren<{ context: MultiContextWithUser }>) => {
  const ldClient = useLDClient()
  useEffect(() => {
    if (isNil(ldClient)) return
    // `identify` clears the persisted flags before refetching, and every tab shares that one entry.
    if (isEqual(ldClient.getContext(), context)) return

    void ldClient.identify(context)
  }, [ldClient, context])

  return <>{children}</>
}

export const LdFeatureFlagProvider = ({ children }: PropsWithChildren) => {
  const { org, user } = useAuthenticatedUser()
  const context = useMemo(() => buildContext(org, user), [org, user])
  const { options, clientId } = getLdConfiguration()

  return (
    <LDProvider
      clientSideID={clientId}
      context={context}
      options={{
        ...options,
        inspectors: createMeticulousInspectors(),
      }}
      reactOptions={{ useCamelCaseFlagKeys: false }}
      timeout={LD_INITIALISATION_TIMEOUT_SECONDS}
    >
      <LaunchDarklyContextUpdater context={context}>{children}</LaunchDarklyContextUpdater>
    </LDProvider>
  )
}
